/**
 * Patient Messages Page - V2 Thread-Based Messaging
 * Transactional marketplace communication interface for:
 * - Lead inquiries
 * - Offer negotiations
 * - Clinic communication
 * - Escalations to TreatX admin
 * 
 * Mobile-responsive design for hybrid app compatibility.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import MessagingAPI, { 
  THREAD_TYPES, PARTICIPANT_ROLES, MESSAGE_TYPES, 
  DELIVERY_STATUS, MESSAGE_CHANNELS 
} from '../../services/MessagingAPI';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, MessageSquare, Send, Image, Paperclip, FileText,
  CheckCheck, Check, AlertTriangle, Building2, User, Shield, ShieldCheck,
  Clock, DollarSign, Calendar, ChevronRight, X, Upload,
  MoreVertical, Flag, Phone, Mail, Lock, Unlock, Info, Search, PenSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { useCommunicationPolicy } from '../../hooks/useCommunicationPolicy';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../components/ui/tooltip';
import api from '../../utils/api';
import { isV3Enabled } from '../../utils/featureFlags';
import NewConversationV3Modal from '../../components/NewConversationV3Modal';
import PurposePicker from '../../components/PurposePicker';
import SecureShareDialog from '../../components/SecureShareDialog';

// ========== HELPER FUNCTIONS ==========

// UAT-MEDICAL-FILE-CARD-OPEN-01 — open a protected medical attachment.
// Always goes through the canonical authenticated API client; a public /
// static medical-file URL is never constructed.
const openProtectedAttachment = async (attachmentId) => {
  if (!attachmentId) return;
  try {
    const r = await api.get(`/attachments/${attachmentId}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* noop */ } }, 60000);
  } catch (err) {
    const status = err?.response?.status;
    toast.error(
      status === 401 || status === 403
        ? 'You are not authorised to open this file'
        : 'Could not open this file',
    );
  }
};

// Auth-aware thumbnail for a protected attachment. A plain <img src> cannot
// authenticate, so we fetch the variant as a blob through the API client.
const ProtectedAttachmentThumb = ({ attachmentId, onClick }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const r = await api.get(`/attachments/${attachmentId}?variant=thumbnail`, {
          responseType: 'blob',
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) setTimeout(() => { try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ } }, 0);
    };
  }, [attachmentId]);

  if (failed) {
    return (
      <div className="w-10 h-10 rounded-md border border-blue-200 bg-white flex items-center justify-center">
        <FileText className="h-4 w-4 text-blue-300" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-10 h-10 rounded-md overflow-hidden border border-blue-200 bg-white"
      data-testid={`shared-file-thumb-${attachmentId}`}
    >
      {url ? (
        <img src={url} alt="Shared file" className="w-full h-full object-cover" />
      ) : (
        <span className="block w-full h-full bg-blue-50" />
      )}
    </button>
  );
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  } else {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
};

const getThreadTypeIcon = (type) => {
  switch (type) {
    case 'lead': return <MessageSquare className="h-4 w-4" />;
    case 'offer': return <DollarSign className="h-4 w-4" />;
    case 'escalation': return <AlertTriangle className="h-4 w-4" />;
    default: return <Building2 className="h-4 w-4" />;
  }
};

const getThreadTypeColor = (type, status) => {
  if (status === 'escalated') return 'bg-red-500';
  switch (type) {
    case 'lead': return 'bg-blue-500';
    case 'offer': return 'bg-emerald-500';
    case 'escalation': return 'bg-orange-500';
    default: return 'bg-slate-500';
  }
};

const getRoleBadgeStyle = (role) => {
  switch (role) {
    case 'patient': return 'bg-purple-100 text-purple-700';
    case 'provider_sales_rep': return 'bg-blue-100 text-blue-700';
    case 'provider_admin': return 'bg-indigo-100 text-indigo-700';
    case 'treatx_admin': return 'bg-emerald-100 text-emerald-700';
    case 'provider_coordinator': return 'bg-teal-100 text-teal-700';
    case 'system': return 'bg-slate-100 text-slate-600';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// OF-001D #3 — consume the canonical role label resolver.
import { roleLabel as canonicalRoleLabel } from '../../utils/canonicalRoleLabels';
const getRoleLabel = (role) => (role === 'system' ? 'System' : canonicalRoleLabel(role));

// ========== STAGE BANNER COMPONENT ==========
// P0-3 FIX: Stage is INFORMATIONAL CONTEXT, not a messaging lock.
// Platform messaging is always available. Only WhatsApp is gated.

// B2: labels/hints come from i18n and mirror the backend communication-policy stage truth
// (stage `inquiry` == a clinic offer has been SENT/VIEWED). No new status authority.
const STAGE_CONFIG = {
  discovery: { color: 'bg-slate-50 border-slate-200 text-slate-700', icon: Search },
  inquiry: { color: 'bg-purple-50 border-purple-200 text-purple-700', icon: FileText },
  negotiation: { color: 'bg-amber-50 border-amber-200 text-amber-700', icon: Shield },
  post_payment: { color: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: Unlock },
};

const StageBanner = ({ policy }) => {
  const { t } = useTranslation();
  if (!policy || !policy.stage) return null;
  const cfg = STAGE_CONFIG[policy.stage];
  if (!cfg) return null;
  const Icon = cfg.icon;

  return (
    <div
      className={`flex items-center gap-2.5 px-4 py-2 border-b text-xs ${cfg.color}`}
      data-testid="stage-banner"
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="font-semibold" data-testid="stage-banner-label">{t(`patient.messages.stage.${policy.stage}.label`)}</span>
      {/* WHATSAPP-TRUTH-01 — the post-payment hint promises WhatsApp. Say so
          only while a WhatsApp sender is actually connected for the clinic
          (canonical `whatsapp_channel_ready` from the communication-policy
          endpoint). The locked business eligibility rules are untouched. */}
      <span className="opacity-70" data-testid="stage-banner-hint">
        {policy.stage === 'post_payment' && policy.whatsapp_channel_ready !== true
          ? t(policy.whatsapp_channel_reason === 'unknown'
              ? 'patient.messages.stage.post_payment.hintWhatsappUnknown'
              : 'patient.messages.stage.post_payment.hintWhatsappUnavailable')
          : t(`patient.messages.stage.${policy.stage}.hint`)}
      </span>
      {policy.relay_active && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="ml-auto flex items-center gap-1 bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-full px-2 py-0.5 text-[10px] font-medium cursor-default"
                data-testid="relay-active-badge"
              >
                <Shield className="h-3 w-3" />
                Masked number
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px] text-center">
              WhatsApp is routed through TreatX — real phone numbers are hidden from both parties.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

// ========== THREAD LIST ITEM COMPONENT ==========

const ThreadListItem = ({ thread, isSelected, onClick, userId }) => {
  const { t } = useTranslation();
  const unreadCount = thread.unread_counts?.[userId] || 0;
  const isEscalated = thread.status === 'escalated';
  
  // BUGFIX: Extract clinic name from participants or thread metadata
  const clinicParticipant = (thread.participants || []).find(p => 
    p.role === 'provider_sales_rep' || p.role === 'provider_admin' || p.role === 'provider_coordinator'
  );
  // TREATX-INQUIRY-REPLY-DELIVERY-01 — a platform support conversation has no
  // clinic; it is TreatX itself answering a Contact enquiry.
  const isPlatformSupport = thread.context_type === 'platform_support';
  const clinicName = isPlatformSupport
    ? t('patient.messages.supportLabel')
    : (clinicParticipant?.name || thread.clinic_name || thread.clinic_id || 'Clinic');
  
  // User-friendly thread type label
  const typeLabel = isPlatformSupport ? t('patient.messages.supportType')
    : thread.context_type === 'offer' ? 'Offer' 
    : thread.context_type === 'lead' ? 'Inquiry'
    : thread.thread_type === 'clinic' ? 'Conversation' 
    : 'Message';
  
  return (
    <div
      onClick={onClick}
      data-testid={`thread-item-${thread.id}`}
      className={`p-4 border-b cursor-pointer transition-all ${
        isSelected 
          ? 'bg-primary/5 border-l-4 border-l-primary' 
          : 'hover:bg-slate-50'
      } ${isEscalated ? 'bg-red-50/50' : ''}`}
    >
      <div className="flex items-start gap-3">
        {/* Thread Type Indicator */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${getThreadTypeColor(thread.thread_type, thread.status)}`}>
          {getThreadTypeIcon(thread.thread_type)}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Clinic Name — primary identifier */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-secondary truncate text-sm" data-testid={`thread-clinic-name-${thread.id}`}>
              {clinicName}
            </h3>
            <span className="text-xs text-foreground/50 whitespace-nowrap">
              {formatTimestamp(thread.last_message_at)}
            </span>
          </div>
          
          {/* Subject + Type Badge */}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-foreground/70 truncate">
              {thread.subject || typeLabel}
            </span>
            {isEscalated && (
              <Badge className="bg-red-500 text-white text-xs">
                Escalated
              </Badge>
            )}
          </div>
          
          {/* Last Message Preview */}
          <p className="text-sm text-foreground/60 truncate mt-1">
            {thread.last_message_preview || 'No messages yet'}
          </p>
        </div>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <Badge
            className="bg-primary text-white"
            data-testid={`unread-badge-${thread.id}`}
          >
            {unreadCount}
          </Badge>
        )}
      </div>
    </div>
  );
};

// ========== CONTEXT HEADER COMPONENT ==========

const ThreadContextHeader = ({ thread, onEscalate, onBack, onWhatsAppUnlock, whatsappUnlocking, policy }) => {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);
  
  // Find clinic participant for display
  const clinicParticipant = thread?.participants?.find(p => 
    p.role === 'provider_sales_rep' || p.role === 'provider_admin'
  );

  const isWhatsAppUnlocked = thread?.whatsapp_unlock_state === 'unlocked';
  // TREATX-WHATSAPP-COMMERCIAL-GATING-09 — the patient-side button must
  // reflect the same commercial-conversion gate enforced on the server.
  // policy.whatsapp_eligible is true only after the deposit milestone
  // (or whichever milestone COMMERCIAL_GATING_MILESTONE points to).
  // WHATSAPP-TRUTH-01 — eligibility alone must not offer a channel that cannot
  // carry the conversation; a disconnected Meta account turns "Continue on
  // WhatsApp" into a dead end for the patient.
  const whatsappEligible = policy?.whatsapp_eligible === true
    && policy?.whatsapp_channel_ready === true;
  
  return (
    <div className="bg-white border-b p-4 sticky top-0 z-10">
      <div className="flex items-center gap-3">
        {/* Back Button (Mobile) */}
        <button 
          onClick={onBack}
          className="lg:hidden p-2 hover:bg-slate-100 rounded-full"
          data-testid="back-to-threads"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        
        {/* Thread Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-secondary truncate" data-testid="thread-header-subject">
              {thread?.subject || 'Conversation'}
            </h2>
            {thread?.status === 'escalated' && (
              <Badge className="bg-red-500 text-white text-xs">Escalated</Badge>
            )}
            {isWhatsAppUnlocked && (
              <Badge className="bg-green-100 text-green-700 text-xs" data-testid="whatsapp-active-badge">
                <Phone className="h-3 w-3 mr-1" /> WhatsApp Active
              </Badge>
            )}
          </div>
          
          {/* Clinic & Treatment Info */}
          {thread?.context_type === 'platform_support' ? (
            <p className="text-sm text-foreground/60 truncate" data-testid="thread-header-platform-support">
              {t('patient.messages.supportContext')}
            </p>
          ) : clinicParticipant && (
            <p className="text-sm text-foreground/60 truncate">
              {clinicParticipant.name} {clinicParticipant.clinic_id ? `· ${t('patient.messages.clinicRepresentative', 'Clinic Representative')}` : ''}
            </p>
          )}
          
          {/* Context Info */}
          {thread?.context_type === 'offer' && (
            <div className="flex items-center gap-2 mt-1">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              <span className="text-sm text-emerald-600 font-medium">{t('patient.messages.offerDiscussion', 'Offer Discussion')}</span>
            </div>
          )}
        </div>

        {/* WhatsApp Unlock Button */}
        {!isWhatsAppUnlocked && whatsappEligible && (
          <button
            onClick={onWhatsAppUnlock}
            disabled={whatsappUnlocking}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs font-medium text-green-700 hover:bg-green-100 transition-colors disabled:opacity-50"
            data-testid="whatsapp-unlock-btn"
          >
            <Phone className="h-3.5 w-3.5" />
            {whatsappUnlocking ? 'Unlocking...' : 'Continue on WhatsApp'}
          </button>
        )}
        {/* TREATX-WHATSAPP-COMMERCIAL-GATING-09 — passive locked state */}
        {!isWhatsAppUnlocked && !whatsappEligible && (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-500 cursor-not-allowed"
            data-testid="whatsapp-unlock-locked"
            title={policy?.whatsapp_eligible === true && policy?.whatsapp_channel_ready !== true
              ? t('patient.messages.whatsappNotConnected')
              : 'Direct WhatsApp contact will be available after your booking deposit is completed. Until then, please continue securely inside TreatX.'}
          >
            <Phone className="h-3.5 w-3.5" />
            <span>{t('patient.messages.secureChat', 'Secure TreatX chat')}</span>
          </div>
        )}
        
        {/* Actions */}
        <div className="relative">
          <button 
            onClick={() => setShowActions(!showActions)}
            className="p-3 hover:bg-slate-100 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
            data-testid="thread-actions-btn"
          >
            <MoreVertical className="h-5 w-5 text-foreground/60" />
          </button>
          
          {showActions && (
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[200px] z-20">
              {thread?.status !== 'escalated' && (
                <button
                  onClick={() => {
                    onEscalate();
                    setShowActions(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-orange-600"
                  data-testid="escalate-thread-btn"
                >
                  <Flag className="h-4 w-4" />
                  Escalate to TreatX Admin
                </button>
              )}
              <button className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Request Callback
              </button>
              <button className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Summary
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== OFFER CARD COMPONENT ==========

const OfferCard = ({ offerRef, onViewDetails, onPay }) => {
  if (!offerRef) return null;
  
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 my-2 max-w-sm">
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className="h-5 w-5 text-emerald-600" />
        <span className="font-semibold text-emerald-800">Treatment Offer</span>
      </div>
      
      <h4 className="font-medium text-secondary mb-1">{offerRef.treatment_name}</h4>
      
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-2xl font-bold text-emerald-600">
          €{offerRef.amount?.toLocaleString()}
        </span>
        <span className="text-sm text-foreground/60">{offerRef.currency}</span>
      </div>
      
      <div className="flex items-center gap-2 mb-3">
        <Badge variant="outline" className={`text-xs ${
          offerRef.status === 'accepted' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
          offerRef.status === 'pending' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
          'bg-slate-100 text-slate-700'
        }`}>
          {offerRef.status}
        </Badge>
        {offerRef.offer_number && (
          <span className="text-xs text-foreground/50">#{offerRef.offer_number}</span>
        )}
      </div>
      
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onViewDetails}
          className="flex-1"
        >
          View Details
        </Button>
        {offerRef.status === 'accepted' && (
          <Button 
            size="sm" 
            onClick={onPay}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="pay-offer-btn"
          >
            Pay Securely
          </Button>
        )}
      </div>
    </div>
  );
};

// ========== PAYMENT REQUEST CARD ==========

const PaymentRequestCard = ({ message, onPay }) => {
  const meta = message.system_metadata || {};
  if (!meta.payment_request) return null;

  return (
    <div className="flex justify-center my-4" data-testid="payment-request-card">
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 max-w-sm w-full shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
            <Shield className="h-4 w-4 text-emerald-600" />
          </div>
          <span className="font-semibold text-emerald-800 text-sm">Clinic is ready to proceed</span>
        </div>

        {meta.treatment_name && (
          <h4 className="font-medium text-secondary text-sm mb-1">{meta.treatment_name}</h4>
        )}

        {/* TREATX-BATCH-B1 — Quote Consumer SSOT Closure.
            This card renders an event-scoped "deposit requested"
            snapshot, NOT the current headline quote price. Read the
            canonical snapshot field deposit_amount_snapshot (written
            once at PAYMENT_REQUESTED by the backend from the canonical
            financial_summary.deposit). The legacy meta.base_price
            field is retired as a display authority — it never represented
            the deposit and it is not re-derived on quote revision. */}
        {(() => {
          const depositAmt = Number.isFinite(meta.deposit_amount_snapshot)
            ? meta.deposit_amount_snapshot
            : null;
          if (depositAmt == null) return null;
          const cur = meta.currency || 'EUR';
          return (
            <div className="flex items-baseline gap-1 mb-1" data-testid="payment-request-deposit-amount">
              <span className="text-2xl font-bold text-emerald-600">
                {cur} {depositAmt.toLocaleString()}
              </span>
              <span className="text-[10px] text-emerald-800/70 ml-1">
                deposit due
              </span>
            </div>
          );
        })()}

        <Button
          onClick={() => onPay(meta.quote_id)}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="pay-securely-btn"
        >
          <DollarSign className="h-4 w-4 mr-1.5" />
          Pay Securely on TreatX
        </Button>

        <p className="text-[10px] text-slate-400 text-center mt-2">
          All payments are processed securely through TreatX
        </p>
      </div>
    </div>
  );
};

// ========== MESSAGE BUBBLE COMPONENT ==========

const MessageBubble = ({ message, isOwn, showSender, userId }) => {
  const isSystem = message.message_type === 'system';
  const isInquiryNote = message.message_type === 'inquiry_note' || message.message_type === 'request_note';
  
  // Inquiry / Request Note — distinct styling, NOT a chat message
  if (isInquiryNote) {
    return (
      <div className="flex justify-center my-4" data-testid={`msg-inquiry-note-${message.id}`}>
        <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-3 max-w-md w-full">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Request Note</span>
          </div>
          <p className="text-sm text-amber-900">{message.content}</p>
          <p className="text-xs text-amber-500 mt-1.5">{formatTimestamp(message.created_at)}</p>
        </div>
      </div>
    );
  }
  
  // System Message
  if (isSystem) {
    const meta = message.system_metadata || {};
    const sharedIds = meta.attachment_ids || [];
    const hasSharedFiles = meta.medical_file_shared && sharedIds.length > 0;
    // TREATX-PROVIDER-DOCUMENT-SHARING-12A: clinic → patient document
    // share card. Same canonical thread + same attachment storage as
    // the patient-share flow; just the receiving direction differs.
    const hasProviderDocs = meta.provider_document_shared && sharedIds.length > 0;
    const isStageChange = message.system_event === 'communication_stage_changed';

    // Stage change notification — distinct styling
    if (isStageChange) {
      const stageCfg = STAGE_CONFIG[meta.new_stage] || {};
      const StageIcon = stageCfg.icon || Info;
      return (
        <div className="flex justify-center my-4" data-testid={`msg-stage-${meta.new_stage}`}>
          <div className={`border rounded-xl px-4 py-2.5 max-w-md flex items-center gap-2.5 ${stageCfg.color || 'bg-slate-50 border-slate-200 text-slate-700'}`}>
            <StageIcon className="h-4 w-4 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">{message.content}</p>
              <p className="text-xs opacity-60 mt-0.5">{formatTimestamp(message.created_at)}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center my-4">
        <div className={`border rounded-xl px-4 py-3 max-w-md ${hasProviderDocs ? 'bg-emerald-50 border-emerald-200' : hasSharedFiles ? 'bg-blue-50 border-blue-200' : 'bg-slate-100 border-slate-200 rounded-full'}`}>
          {hasProviderDocs ? (
            <div className="text-left" data-testid="msg-provider-document-card">
              <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                Document shared with you
              </p>
              {Array.isArray(meta.file_names) && meta.file_names.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {meta.file_names.slice(0, 4).map((fn, i) => {
                    const aid = sharedIds[i];
                    return (
                      <li key={i} className="text-xs text-emerald-900 flex items-center gap-1.5">
                        <FileText className="h-3 w-3 text-emerald-600 shrink-0" />
                        {aid ? (
                          <button
                            type="button"
                            onClick={() => openProtectedAttachment(aid)}
                            className="underline hover:text-emerald-700 truncate text-left"
                            data-testid={`provider-doc-link-${aid}`}
                          >
                            {fn}
                          </button>
                        ) : (
                          <span className="truncate">{fn}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {meta.clinic_display_name && (
                <p className="text-xs text-emerald-700 mt-1.5">
                  <span className="text-emerald-500">From:</span>{' '}
                  <span className="font-medium">{meta.clinic_display_name}</span>
                </p>
              )}
              {meta.document_category && (
                <p className="text-[10px] text-emerald-600 mt-0.5 uppercase tracking-wide">
                  {String(meta.document_category).replace(/_/g, ' ')}
                </p>
              )}
              {meta.notes && (
                <p className="text-xs text-emerald-800 mt-1.5 italic">"{meta.notes}"</p>
              )}
              <p className="text-[10px] text-emerald-500 mt-1.5">{formatTimestamp(message.created_at)}</p>
            </div>
          ) : hasSharedFiles ? (
            <div className="text-left" data-testid="msg-shared-file-card">
              <p className="text-sm font-semibold text-blue-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                Medical file shared securely
              </p>
              {/* TREATX-10B-UX-ACCEPTANCE-REMEDIATION-02: render the
                  enriched file names + clinic name in the card so the
                  patient sees full context without any internal IDs. */}
              {Array.isArray(meta.file_names) && meta.file_names.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {meta.file_names.slice(0, 4).map((fn, i) => {
                    const aid = sharedIds[i];
                    return (
                      <li key={i} className="text-xs text-blue-900 flex items-center gap-1.5">
                        <FileText className="h-3 w-3 text-blue-500 shrink-0" />
                        {aid ? (
                          <button
                            type="button"
                            onClick={() => openProtectedAttachment(aid)}
                            className="underline hover:text-blue-700 truncate text-left"
                            data-testid={`shared-file-link-${aid}`}
                          >
                            {fn}
                          </button>
                        ) : (
                          <span className="truncate">{fn}</span>
                        )}
                      </li>
                    );
                  })}
                  {meta.file_names.length > 4 && (
                    <li className="text-[10px] text-blue-500">+{meta.file_names.length - 4} more</li>
                  )}
                </ul>
              )}
              {Array.isArray(meta.recipient_names) && meta.recipient_names.length > 0 && (
                <p className="text-xs text-blue-700 mt-1.5" data-testid="shared-file-recipients">
                  <span className="text-blue-500">Can open this file:</span>{' '}
                  <span className="font-medium">{meta.recipient_names.join(', ')}</span>
                </p>
              )}
              {meta.clinic_display_name && (
                <p className="text-xs text-blue-700 mt-1.5">
                  <span className="text-blue-500">Shared with:</span>{' '}
                  <span className="font-medium">{meta.clinic_display_name}</span>
                </p>
              )}
              <p className="text-[10px] text-blue-500 mt-1.5">
                Access remains active until you stop sharing.
              </p>
              {sharedIds.length > 0 && (
                <div className="flex gap-1.5 mt-2" data-testid="shared-file-previews">
                  {sharedIds.slice(0, 4).map((aid) => (
                    <ProtectedAttachmentThumb
                      key={aid}
                      attachmentId={aid}
                      onClick={() => openProtectedAttachment(aid)}
                    />
                  ))}
                </div>
              )}
              <p className="text-[10px] text-blue-400 mt-1.5">{formatTimestamp(message.created_at)}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-600 text-center">
              {message.content}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Regular Message
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[80%] md:max-w-[70%] ${isOwn ? 'order-1' : ''}`}>
        {/* Sender Label */}
        {showSender && !isOwn && (
          <div className="flex items-center gap-2 mb-1 ml-1">
            <Badge className={`text-xs ${getRoleBadgeStyle(message.sender_role)}`}>
              {getRoleLabel(message.sender_role)}
            </Badge>
            <span className="text-xs text-foreground/50">{message.sender_name}</span>
          </div>
        )}
        
        {/* Message Content */}
        <div className={`rounded-2xl px-4 py-3 ${
          isOwn 
            ? 'bg-primary text-white rounded-br-md' 
            : 'bg-white border shadow-sm rounded-bl-md'
        }`}>
          <p className={`text-sm whitespace-pre-wrap ${isOwn ? 'text-white' : 'text-secondary'}`}>
            {message.content}
          </p>
          
          {/* Attachments */}
          {message.attachments?.length > 0 && (
            <div className="mt-2 space-y-2">
              {message.attachments.map((att, i) => (
                <AttachmentPreview key={i} attachment={att} isOwn={isOwn} />
              ))}
            </div>
          )}
          
          {/* Offer Reference */}
          {message.offer_reference && (
            <OfferCard 
              offerRef={message.offer_reference}
              onViewDetails={() => {}}
              onPay={() => {}}
            />
          )}
        </div>
        
        {/* Timestamp & Delivery Status */}
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'} px-1`}>
          <span className="text-xs text-foreground/40">
            {formatTimestamp(message.created_at)}
          </span>
          {isOwn && (
            <DeliveryStatusIcon status={message.overall_status} />
          )}
        </div>
      </div>
    </div>
  );
};

// ========== ATTACHMENT PREVIEW COMPONENT ==========

const AttachmentPreview = ({ attachment, isOwn }) => {
  const isImage = attachment.file_type?.startsWith('image/');
  
  if (isImage) {
    return (
      <div className="rounded-lg overflow-hidden">
        <img 
          src={attachment.thumbnail_url || attachment.file_url}
          alt={attachment.filename}
          className="max-w-[200px] max-h-[200px] object-cover rounded-lg"
        />
      </div>
    );
  }
  
  // File attachment
  return (
    <a 
      href={attachment.file_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 p-2 rounded-lg ${
        isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-100 hover:bg-slate-200'
      }`}
    >
      <FileText className={`h-5 w-5 ${isOwn ? 'text-white' : 'text-slate-600'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isOwn ? 'text-white' : 'text-secondary'}`}>
          {attachment.filename}
        </p>
        <p className={`text-xs ${isOwn ? 'text-white/70' : 'text-foreground/50'}`}>
          {(attachment.file_size / 1024).toFixed(1)} KB
        </p>
      </div>
    </a>
  );
};

// ========== DELIVERY STATUS ICON ==========

const DeliveryStatusIcon = ({ status }) => {
  switch (status) {
    case 'read':
      return <CheckCheck className="h-3 w-3 text-blue-500" />;
    case 'delivered':
      return <CheckCheck className="h-3 w-3 text-foreground/40" />;
    case 'sent':
      return <Check className="h-3 w-3 text-foreground/40" />;
    case 'failed':
      return <AlertTriangle className="h-3 w-3 text-red-500" />;
    default:
      return <Clock className="h-3 w-3 text-foreground/40" />;
  }
};

// ========== MESSAGE INPUT COMPONENT ==========

const MessageInput = ({ onSend, disabled, threadId, threadClinicId, threadClinicName, onUploaded }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSend({ content: message.trim(), type: 'text' });
      setMessage('');
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const allowed = ['jpg', 'jpeg', 'png', 'pdf'];
    const invalid = selectedFiles.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return !allowed.includes(ext);
    });
    if (invalid.length) {
      toast.error(`Only JPG, PNG, PDF files allowed. Rejected: ${invalid.map(f => f.name).join(', ')}`);
      return;
    }
    if (selectedFiles.some(f => f.size > 20 * 1024 * 1024)) {
      toast.error('Max file size is 20MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach(f => formData.append('files', f));

      // TREATX-SECURE-MEDICAL-FILE-SHARING-10B: **Upload ≠ Share**.
      // Step 1 — upload puts the file into the patient's private vault
      // ONLY. No `attachment_access` grant is created here. The clinic
      // cannot see the file yet.
      const uploadRes = await api.post(`/patient/medical-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadData = uploadRes.data;

      if (uploadData?.success) {
        const uploaded = (uploadData.files || []).filter(f => f.attachment_id);
        if (!uploaded.length) {
          toast.error('Upload failed', { description: 'No files were saved.' });
          return;
        }
        // Step 2 — hand off to the parent which will open the Secure
        // Share dialog. Sharing requires explicit, deliberate consent
        // and is the ONLY path that creates an `attachment_access`
        // grant.
        toast.success(`${uploaded.length} file(s) uploaded to your vault — confirm sharing on the next screen.`);
        if (onUploaded) onUploaded(uploaded);
      } else {
        toast.error(uploadData?.detail || uploadData?.error || 'Upload failed');
      }
    } catch (err) {
      console.error('File upload error:', err);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className="bg-white border-t p-4 sticky bottom-0">
      <div className="flex items-end gap-2">
        {/* Attachment Button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2 hover:bg-slate-100 rounded-full text-foreground/60 hover:text-foreground"
          disabled={disabled}
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept=".jpg,.jpeg,.png,.pdf"
        />
        
        {/* Image Button */}
        <button
          onClick={() => {
            fileInputRef.current?.click();
          }}
          className="p-2 hover:bg-slate-100 rounded-full text-foreground/60 hover:text-foreground"
          disabled={disabled}
        >
          <Image className="h-5 w-5" />
        </button>
        
        {/* Message Input */}
        <div className="flex-1">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('patient.messages.typeMessage', 'Type a message…')}
            rows={1}
            className="w-full px-4 py-3 border rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            disabled={disabled}
            data-testid="message-input"
          />
        </div>
        
        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={!message.trim() || disabled}
          className="rounded-full h-12 w-12 p-0"
          data-testid="send-message-btn"
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

// ========== ESCALATION MODAL ==========

const EscalationModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            <Flag className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-secondary">Escalate to TreatX Admin</h3>
            <p className="text-sm text-foreground/60">Request platform intervention</p>
          </div>
        </div>
        
        <p className="text-sm text-foreground/70 mb-4">
          A TreatX administrator will review your conversation and assist with any issues.
        </p>
        
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please describe your concern..."
          rows={3}
          className="w-full px-4 py-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 mb-4"
        />
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || loading}
            className="flex-1 bg-orange-600 hover:bg-orange-700"
          >
            {loading ? 'Escalating...' : 'Escalate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ========== EMPTY STATE COMPONENT ==========

const EmptyThreads = () => {
  const { t } = useTranslation();
  return (
  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
      <MessageSquare className="h-8 w-8 text-slate-400" />
    </div>
    <h3 className="font-semibold text-secondary mb-2">{t('patient.messages.empty').split(' — ')[0]}</h3>
    <p className="text-sm text-foreground/60 max-w-xs">
      {t('patient.messages.empty')}
    </p>
  </div>
  );
};

const EmptyConversation = () => {
  const { t } = useTranslation();
  return (
  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
      <MessageSquare className="h-8 w-8 text-primary" />
    </div>
    <h3 className="font-semibold text-secondary mb-2">{t('patient.messages.newMessage')}</h3>
    <p className="text-sm text-foreground/60">
      {t('patient.messages.empty')}
    </p>
  </div>
  );
};

// ========== MAIN COMPONENT ==========

const PatientMessagesV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  
  // Navigation state from Offers/Orders
  const navState = location.state || {};
  
  // State
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMobileThreads, setShowMobileThreads] = useState(true);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [showNewThreadForm, setShowNewThreadForm] = useState(false);
  const [newThreadSubject, setNewThreadSubject] = useState('');
  const [newThreadMessage, setNewThreadMessage] = useState('');
  const [creatingThread, setCreatingThread] = useState(false);
  const [showV3NewConversation, setShowV3NewConversation] = useState(false);

  // TREATX-SECURE-MEDICAL-FILE-SHARING-10B: Secure Share dialog state.
  // Files are uploaded into the patient's vault first, then this dialog
  // opens to capture explicit consent before any `attachment_access`
  // grant is created. Recipient (clinic) is auto-resolved from the
  // selected thread — patient never sees clinic_id / thread_id.
  const [secureShareOpen, setSecureShareOpen] = useState(false);
  const [secureShareFiles, setSecureShareFiles] = useState([]);
  // SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01 — eligible recipient PEOPLE for
  // the current conversation, resolved server-side from the patient's
  // persisted relationships.
  const [shareRecipientPeople, setShareRecipientPeople] = useState([]);
  
  // Communication policy for the selected thread
  const { policy, refetch: refetchPolicy } = useCommunicationPolicy(selectedThread?.id);
  
  const userId = user?.id || 'patient_demo';
  
  // ── B-2.1 / A — Patient fetcher dedup ─────────────────────────
  //
  // Root cause (parity with provider-side INV-M11): `fetchThreads`
  // is stable via `useCallback([userId])`, but React.StrictMode dev
  // double-mount still invokes the initial-load `useEffect` twice at
  // t=0 → two simultaneous `getUserThreads` requests. We can't rely
  // on identity stability alone; we need a single-flight promise ref
  // so a second concurrent call joins the first instead of firing a
  // duplicate. Same shape as the provider-side `pendingResetPromiseRef`
  // but scoped to the patient inbox lifecycle (which has neither
  // paged pagination nor a scope selector — just a single canonical
  // fetch).
  const pendingThreadsFetchRef = useRef(null);
  const fetchThreads = useCallback(async () => {
    if (pendingThreadsFetchRef.current) {
      // A fetch is already in flight (StrictMode double-mount, back-
      // to-back deep-link handlers, etc.). Join it — no duplicate
      // network request.
      return pendingThreadsFetchRef.current;
    }
    const _work = (async () => {
      try {
        setLoading(true);
        const response = await MessagingAPI.getUserThreads({ userId });
        setThreads(response.threads || []);
        setUnreadCount(response.unread_count || 0);
      } catch (error) {
        console.error('Error fetching threads:', error);
        toast.error('Failed to load conversations');
      } finally {
        setLoading(false);
        pendingThreadsFetchRef.current = null;
      }
    })();
    pendingThreadsFetchRef.current = _work;
    return _work;
  }, [userId]);
  
  // Fetch messages for selected thread
  const fetchMessages = useCallback(async (threadId) => {
    try {
      setMessagesLoading(true);
      const response = await MessagingAPI.getMessages({ threadId });
      setMessages(response.messages || []);
      setSelectedThread(response.thread);
      
      // Mark as read
      await MessagingAPI.markMessagesRead({ threadId, userId });

      // ── B-2 / M1.a — local unread patch, no full refetch ─────────
      // Owner Audit §3 M1 root cause: this used to call
      // `fetchThreads()` unconditionally on every thread select, which
      // flipped the global `loading` flag and wiped the thread panel
      // with "Loading conversations…" every 1–2 seconds. The unread
      // counter is already reset server-side by `markMessagesRead`; we
      // just mirror that locally so the badge disappears without a
      // network round-trip and without disturbing thread-list identity.
      setThreads(prev => prev.map(t =>
        t.id === threadId
          ? { ...t, unread_counts: { ...(t.unread_counts || {}), [userId]: 0 } }
          : t
      ));
      setUnreadCount(prev => {
        const decrement = (response.thread?.unread_counts?.[userId]) || 0;
        return Math.max(0, prev - decrement);
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, [userId]);
  
  // Initial load - always fetch for demo, or when authenticated
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);
  
  // TREATX-BATCH-1.2 — track the threadId we've already applied from
  // the URL so the URL-selection effect never re-selects a thread on
  // subsequent renders. Prior behaviour (deps: [searchParams, threads])
  // deep-link → handleSelectThread → fetchMessages → fetchThreads() →
  // new `threads` array identity → effect re-runs → loop.
  const appliedDeepLinkRef = useRef(null);
  // M-B — capture nav-state context before the effect wipes it, so
  // `handleCreateThread` still sees {clinicId, contextId, contextType,
  // subject} when the user submits the modal. Prior version cleared
  // `location.state` in the same effect that opened the modal, so the
  // create payload degraded to {thread_type:'clinic'} → HTTP 400.
  const pendingContextRef = useRef(null);

  // Handle thread selection from URL
  useEffect(() => {
    const threadId = searchParams.get('thread') || searchParams.get('threadId');
    if (!threadId || threads.length === 0) return;
    if (appliedDeepLinkRef.current === threadId) return;
    const thread = threads.find(t => t.id === threadId);
    if (thread) {
      appliedDeepLinkRef.current = threadId;
      handleSelectThread(thread);
    }
  }, [searchParams, threads]);
  
  // Handle navigation state from Offers/Orders — auto-open new thread form
  useEffect(() => {
    if (navState.clinicId && !loading && threads.length >= 0) {
      // TREATX-BATCH-1.2 — the navState.contextId is a QUOTE id
      // (from `Offer Detail → Open Messages`). It can NEVER match
      // a `case_id`, so the prior comparison always returned false
      // and the "Start New Conversation" form opened even when a
      // canonical thread already existed. Reuse existing threads
      // for the same clinic + offer context by matching the SAME
      // sources the backend dedups on:
      //   1. offer/quote_id      (thread.context_id / thread.contextId)
      //   2. lead_id             (thread.context_id)
      //   3. clinic_id           (fallback for the very first thread)
      // If none matches we correctly open the new-thread form.
      const skipQuery = Boolean(
        searchParams.get('thread') || searchParams.get('threadId')
      );
      if (skipQuery) {
        // A specific thread was requested via URL — the URL-selector
        // above handles it; do not simultaneously open the
        // new-thread modal.
        // ── B-2 / M1.b — clear location.state via react-router so this
        // effect never re-fires on subsequent renders. `history.replaceState`
        // alone doesn't clear React-Router's `location.state`, so `navState`
        // stayed truthy → effect re-ran on every `threads` mutation →
        // showNewThreadForm flip-flopping (compounding flicker root cause).
        navigate(location.pathname, { replace: true, state: {} });
        return;
      }
      const existingThread = threads.find(t =>
        t.clinic_id === navState.clinicId && (
          (navState.contextId && (t.context_id === navState.contextId || t.contextId === navState.contextId))
          || (!navState.contextId)
        )
      );
      if (existingThread) {
        handleSelectThread(existingThread);
      } else {
        // ── M-B — snapshot the anchor BEFORE clearing location.state.
        // handleCreateThread reads from `pendingContextRef.current`
        // instead of the stale `navState`.
        pendingContextRef.current = {
          clinicId: navState.clinicId,
          contextId: navState.contextId,
          contextType: navState.contextType,
          subject: navState.subject,
        };
        setShowNewThreadForm(true);
        setNewThreadSubject(navState.subject || 'New message');
      }
      // ── B-2 / M1.b — clear location.state via react-router (idempotent).
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [navState, loading, threads, searchParams, navigate, location.pathname]);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Handle thread selection
  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
    setShowMobileThreads(false);
    fetchMessages(thread.id);
    // M-D — persist selected thread across full-page reload via URL
    // param so the picker + selected pane re-hydrate on refresh.
    // Uses `replace` so the browser history isn't polluted.
    try {
      const sp = new URLSearchParams(window.location.hash.split('?')[1] || '');
      sp.set('thread', thread.id);
      const base = window.location.hash.split('?')[0] || '#/patient/messages';
      window.history.replaceState(null, '', `${base}?${sp.toString()}`);
    } catch { /* no-op */ }
  };
  
  // Handle create new thread (from New Thread form or navigation state)
  const handleCreateThread = async () => {
    if (!newThreadSubject.trim() || !newThreadMessage.trim()) {
      toast.error('Please enter a subject and message');
      return;
    }
    try {
      setCreatingThread(true);

      // ── M-B — read the captured context (survives location.state clear).
      const ctx = pendingContextRef.current || {};

      // Map context type to valid ThreadType enum
      const threadTypeMap = {
        'offer': 'offer',
        'order': 'clinic',
        'lead': 'lead',
      };
      const threadType = threadTypeMap[ctx.contextType] || 'clinic';

      const response = await MessagingAPI.createThread({
        threadType,
        contextId: ctx.contextId || undefined,
        contextType: ctx.contextType || undefined,
        clinicId: ctx.clinicId || undefined,
        subject: newThreadSubject,
        initialMessage: newThreadMessage, // String, not object
      });
      if (response.thread) {
        setShowNewThreadForm(false);
        setNewThreadSubject('');
        setNewThreadMessage('');
        pendingContextRef.current = null;
        // ── B-2 / M1.b — clear location.state via react-router.
        navigate(location.pathname, { replace: true, state: {} });
        await fetchThreads();
        handleSelectThread(response.thread);
        toast.success('Conversation started');
      }
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to start conversation');
    } finally {
      setCreatingThread(false);
    }
  };

  // Handle send message
  const handleSendMessage = async ({ content, type }) => {
    if (!selectedThread) return;
    
    try {
      setSending(true);
      await MessagingAPI.sendMessage({
        threadId: selectedThread.id,
        senderId: userId,
        senderRole: 'patient',
        senderName: user?.name || 'Patient',
        content,
        messageType: type
      });
      
      // Refresh messages
      await fetchMessages(selectedThread.id);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  // Handle escalation
  const handleEscalate = async (reason) => {
    if (!selectedThread) return;
    
    try {
      setEscalating(true);
      await MessagingAPI.escalateThread({
        threadId: selectedThread.id,
        adminId: 'treatx_admin',
        reason
      });
      
      toast.success('Thread escalated to TreatX Admin');
      setShowEscalationModal(false);
      
      // Refresh
      await fetchMessages(selectedThread.id);
    } catch (error) {
      console.error('Error escalating thread:', error);
      toast.error('Failed to escalate thread');
    } finally {
      setEscalating(false);
    }
  };

  // WhatsApp unlock handler
  const [whatsappUnlocking, setWhatsappUnlocking] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const handleWhatsAppUnlock = async () => {
    if (!selectedThread) return;

    // Check consent first
    try {
      const token = '';
      const consentRes = await api.get(`/patient/consent/messaging`, {
      });
      if (consentRes.ok) {
        const consentData = consentRes.data;
        if (!consentData.whatsapp_consent) {
          setShowConsentModal(true);
          return;
        }
      }
    } catch (e) {
      // If consent check fails, show consent modal to be safe
      setShowConsentModal(true);
      return;
    }

    await executeWhatsAppUnlock();
  };

  const executeWhatsAppUnlock = async () => {
    if (!selectedThread) return;
    setWhatsappUnlocking(true);
    try {
      const token = '';
      const res = await api.post(`/v2/threads/${selectedThread.id}/whatsapp-unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (false) { /* axios throws on error */
        const err = res.data.catch(() => ({}));
        throw new Error(err.message || err.error || 'Failed to unlock WhatsApp');
      }
      toast.success('WhatsApp channel activated! Messages will now include WhatsApp.');
      await fetchMessages(selectedThread.id);
    } catch (e) {
      toast.error(e.message || 'Could not unlock WhatsApp');
    } finally {
      setWhatsappUnlocking(false);
    }
  };

  const handleConsentAndUnlock = async () => {
    try {
      const token = '';
      await api.post(`/patient/consent/messaging`);
      setShowConsentModal(false);
      await executeWhatsAppUnlock();
    } catch (e) {
      toast.error('Failed to record consent');
    }
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Note: Auth check removed for demo purposes - in production, re-enable:
  // if (!isAuthenticated) { return <LoginPrompt /> }
  
  return (
    // FASTFIX-CANONICAL-MESSAGING-VISIBILITY-01 (2026-06-12):
    // The outer App layout already renders a fixed-height Header and gives
    // <main> the remaining viewport via flex-1 + pt-20. Using h-screen here
    // makes this page 100vh tall — taller than its <main> container — so
    // the bottom (where the composer lives) gets clipped by main's
    // overflow-hidden. Use h-[calc(100vh-80px)] to mirror ClinicMessagesV2
    // and pin the composer inside the visible viewport.
    <div className="h-[calc(100vh-80px)] bg-background flex flex-col overflow-hidden" data-testid="patient-messages-page">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
        <button onClick={() => navigate('/patient/dashboard')} className="p-2 hover:bg-slate-100 rounded-full">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-semibold text-secondary">{t('patient.messages.title')}</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-foreground/60">{unreadCount} {t('patient.messages.unread').toLowerCase()}</p>
          )}
        </div>
      </header>
      
      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Thread List */}
        <div className={`w-full lg:w-80 xl:w-96 border-r bg-white flex-shrink-0 overflow-y-auto ${
          showMobileThreads ? 'block' : 'hidden lg:block'
        }`}>
          {/* New Conversation Button */}
          <div className="p-3 border-b">
            <Button
              onClick={() => isV3Enabled() ? setShowV3NewConversation(true) : setShowNewThreadForm(true)}
              className="w-full bg-primary text-white"
              size="sm"
              data-testid="new-conversation-btn"
            >
              <PenSquare className="h-4 w-4 mr-2" /> {t('patient.messages.newConversation', 'New Conversation')}
            </Button>
          </div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              <p className="text-sm text-foreground/60 mt-2">Loading conversations...</p>
            </div>
          ) : threads.length === 0 ? (
            <EmptyThreads />
          ) : (
            <div data-testid="thread-list">
              {threads.map(thread => (
                <ThreadListItem
                  key={thread.id}
                  thread={thread}
                  isSelected={selectedThread?.id === thread.id}
                  onClick={() => handleSelectThread(thread)}
                  userId={userId}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Conversation View */}
        <div className={`flex-1 flex flex-col bg-slate-50 ${
          showMobileThreads ? 'hidden lg:flex' : 'flex'
        }`}>
          {selectedThread ? (
            <>
              {/* M-D — purpose picker for this journey. Server-authoritative. */}
              {selectedThread.conversation_group_id && (
                <PurposePicker
                  conversationGroupId={selectedThread.conversation_group_id}
                  currentThreadId={selectedThread.id}
                  onSelectThread={(t) => handleSelectThread(t)}
                />
              )}
              {/* Context Header */}
              <ThreadContextHeader 
                thread={selectedThread}
                onEscalate={() => setShowEscalationModal(true)}
                onBack={() => setShowMobileThreads(true)}
                onWhatsAppUnlock={handleWhatsAppUnlock}
                whatsappUnlocking={whatsappUnlocking}
                policy={policy}
              />
              
              {/* Stage Banner */}
              <StageBanner policy={policy} />
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4">
                {messagesLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <>
                    {messages.map((msg, idx) => {
                      const isOwn = msg.sender_id === userId;
                      const showSender = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;

                      // Render payment request card for payment_requested system messages
                      if (msg.message_type === 'system' && msg.system_event === 'payment_requested' && msg.system_metadata?.payment_request) {
                        return (
                          <PaymentRequestCard
                            key={msg.id}
                            message={msg}
                            onPay={(quoteId) => navigate(`/patient/payment?quote_id=${quoteId}`)}
                          />
                        );
                      }
                      
                      return (
                        <MessageBubble
                          key={msg.id}
                          message={msg}
                          isOwn={isOwn}
                          showSender={showSender}
                          userId={userId}
                        />
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Message Input — P0-3 FIX: Always show. Platform messaging is always open.
                  TREATX-SECURE-MEDICAL-FILE-SHARING-10B: file upload is now
                  decoupled from share. After upload, MessageInput calls
                  onUploaded → we open the Secure Share dialog with the
                  current thread's clinic auto-resolved as recipient. */}
              <MessageInput 
                onSend={handleSendMessage}
                disabled={sending || selectedThread.status === 'resolved'}
                threadId={selectedThread?.id}
                threadClinicId={selectedThread?.participants?.find(p => p.role === 'provider_admin' || p.role === 'clinic')?.id || selectedThread?.clinic_id}
                threadClinicName={(() => {
                  const p = (selectedThread?.participants || []).find(p => p.role === 'provider_sales_rep' || p.role === 'provider_admin' || p.role === 'provider_coordinator');
                  return p?.name || selectedThread?.clinic_name || 'Clinic';
                })()}
                onUploaded={async (uploadedFiles) => {
                  // UAT-MEDICAL-JOURNEY-SCOPED-AUTHZ-01 — ask for the
                  // recipients OF THIS EXACT CONVERSATION. The backend
                  // resolves thread → conversation group → journey and
                  // returns only that journey's (user_id, role)
                  // relationships, so a sibling journey at the same
                  // Provider can never leak in.
                  setSecureShareFiles(uploadedFiles);
                  try {
                    const r = await api.get('/patient/share-recipients', {
                      params: { thread_id: selectedThread?.id },
                    });
                    const list = (r?.data?.recipients) || [];
                    const match = list[0];
                    if (match) {
                      setSelectedThread(prev => prev ? {
                        ...prev,
                        clinic_name: match.clinic_name,
                      } : prev);
                    }
                    setShareRecipientPeople(match?.people || []);
                  } catch (e) { setShareRecipientPeople([]); }
                  setSecureShareOpen(true);
                }}
              />
            </>
          ) : (
            <EmptyConversation />
          )}
        </div>
      </div>
      
      {/* Escalation Modal */}
      <EscalationModal
        isOpen={showEscalationModal}
        onClose={() => setShowEscalationModal(false)}
        onConfirm={handleEscalate}
        loading={escalating}
      />

      {/* New Thread Form Modal */}
      {showNewThreadForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="new-thread-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-secondary">Start New Conversation</h3>
            
            {/* P1-C FIX: Show clinic/context info when navigated from Offers/Orders */}
            {navState.clinicId && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid="thread-context-banner">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary truncate">
                    {navState.contextType === 'offer' ? 'Re: Offer Inquiry' : 
                     navState.contextType === 'order' ? 'Re: Order Communication' : 
                     'Clinic Message'}
                  </p>
                  <p className="text-xs text-foreground/50 truncate">
                    {navState.subject || 'Treatment inquiry'}
                  </p>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1">Subject</label>
                <input
                  type="text"
                  value={newThreadSubject}
                  onChange={(e) => setNewThreadSubject(e.target.value)}
                  placeholder="e.g., Question about my treatment"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="new-thread-subject"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1">Message</label>
                <textarea
                  value={newThreadMessage}
                  onChange={(e) => setNewThreadMessage(e.target.value)}
                  placeholder={t('patient.messages.typeMessageToClinic', 'Type your message to the clinic…')}
                  rows={5}
                  className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="new-thread-message"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowNewThreadForm(false); setNewThreadSubject(''); setNewThreadMessage(''); }} data-testid="cancel-new-thread">
                Cancel
              </Button>
              <Button onClick={handleCreateThread} disabled={creatingThread || !newThreadSubject.trim() || !newThreadMessage.trim()} data-testid="send-new-thread">
                <Send className="h-4 w-4 mr-2" />
                {creatingThread ? 'Sending...' : 'Start Conversation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Consent Modal */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="whatsapp-consent-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Phone className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-secondary">WhatsApp Consent Required</h3>
            </div>
            <p className="text-sm text-foreground/60">
              To continue this conversation on WhatsApp, we need your consent. Your identity will remain masked — clinics will never see your phone number, name, or email.
            </p>
            <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer" data-testid="consent-checkbox-label">
              <input type="checkbox" id="wa-consent" className="mt-0.5 accent-green-600" data-testid="whatsapp-consent-checkbox" />
              <span className="text-xs text-foreground/70">
                I allow clinics to contact me via TreatX messaging channels (including WhatsApp). My contact details will remain private and masked.
              </span>
            </label>
            <p className="text-[10px] text-foreground/40">
              For your security, all payments must be made on TreatX. Do not send money directly to clinics.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConsentModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-slate-50"
                data-testid="consent-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const cb = document.getElementById('wa-consent');
                  if (!cb?.checked) {
                    toast.error('Please check the consent box to continue');
                    return;
                  }
                  handleConsentAndUnlock();
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                data-testid="consent-confirm-btn"
              >
                Agree & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V3 New Conversation Modal */}
      <NewConversationV3Modal
        isOpen={showV3NewConversation}
        onClose={() => setShowV3NewConversation(false)}
        onThreadCreated={(thread) => {
          fetchThreads();
          handleSelectThread(thread);
        }}
        userId={userId}
        userRole="patient"
        prefilledClinicId={navState.clinicId || null}
        prefilledCaseId={navState.contextId || null}
        prefilledSubject={navState.subject || ''}
      />

      {/* TREATX-SECURE-MEDICAL-FILE-SHARING-10B: Secure Share dialog.
          Opens after upload with the active thread's clinic auto-resolved
          as the recipient. The patient never sees clinic_id / thread_id —
          only the human-readable clinic name. This is the ONLY path that
          creates an `attachment_access` grant from the messaging UI.
          Conditionally mounted so every open starts with clean state. */}
      {secureShareOpen && (
        <SecureShareDialog
          open={secureShareOpen}
          onClose={() => {
            setSecureShareOpen(false);
            setSecureShareFiles([]);
          }}
          onShared={() => {
            if (selectedThread?.id) fetchMessages(selectedThread.id);
          }}
          files={secureShareFiles}
          surface="patient_messages"
          recipient={(() => {
            if (!selectedThread) return null;
            const clinicParticipant = (selectedThread.participants || []).find(
              p => p.role === 'provider_sales_rep' || p.role === 'provider_admin' || p.role === 'provider_coordinator'
            );
            const clinicId =
              clinicParticipant?.id ||
              selectedThread.clinic_id ||
              (selectedThread.participants || []).find(p => p.role === 'clinic')?.id;
            if (!clinicId) return null;
            // Priority: name we just resolved from /share-recipients
            // > participants name (may be an email — last resort).
            // We *never* fall back to clinic_id; "Clinic" is the
            // generic fallback so the UI never leaks a raw ID.
            const isLikelyEmail = (s) => typeof s === 'string' && s.includes('@');
            const isLikelyRawId = (s) => typeof s === 'string' && /^prov-[a-z0-9-]+/i.test(s);
            const candidate = selectedThread.clinic_name;
            const safeName =
              (candidate && !isLikelyRawId(candidate)) ? candidate :
              (clinicParticipant?.name && !isLikelyEmail(clinicParticipant.name) && !isLikelyRawId(clinicParticipant.name)) ? clinicParticipant.name :
              'Clinic';
            return {
              clinic_id: clinicId,
              clinic_name: safeName,
              thread_id: selectedThread.id,
              // Person-level recipients — the patient must see and pick
              // exactly who will be able to open the file.
              people: shareRecipientPeople,
            };
          })()}
        />
      )}
    </div>
  );
};

export default PatientMessagesV2;
